import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Target, TrendingUp, Shield, Building2, 
  Search, DollarSign, Wrench, Users, BarChart3, 
  RefreshCw, CheckCircle2, ChevronRight, LineChart,
  Home, Briefcase, Clock, Layers, Scale
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const StrategyPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const scaleInVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Strategy pillars with shorter headers
  const strategyPillars = [
    {
      icon: Search,
      title: 'Identifying Opportunities',
      subtitle: 'Distressed & Undermanaged Assets',
      description: 'We source off-market opportunities through proprietary relationships, targeting properties with operational inefficiencies and market dislocations that create value-add potential.',
      points: ['Off-market deal sourcing', 'Operational inefficiency analysis', 'Market dislocation identification'],
      color: 'from-blue-500/20 to-blue-500/5'
    },
    {
      icon: DollarSign,
      title: 'Disciplined Acquisitions',
      subtitle: 'Below-Market Entry Points',
      description: 'Conservative underwriting with built-in margin of safety ensures downside protection while positioning for outsized returns through disciplined capital deployment.',
      points: ['Conservative assumptions', 'Downside protection', 'Margin of safety focus'],
      color: 'from-emerald-500/20 to-emerald-500/5'
    },
    {
      icon: Wrench,
      title: 'Operational Excellence',
      subtitle: 'Cost Optimization & Efficiency',
      description: 'In-house property management and operational expertise drive NOI improvements through expense controls, vendor efficiencies, and institutional-grade systems.',
      points: ['In-house management', 'Expense optimization', 'Vendor consolidation'],
      color: 'from-amber-500/20 to-amber-500/5'
    },
    {
      icon: Building2,
      title: 'Physical Repositioning',
      subtitle: 'Renovations & Market Upgrades',
      description: 'Strategic unit upgrades, amenity improvements, and property repositioning capture rent premiums and attract higher-quality tenants in target submarkets.',
      points: ['Unit renovations', 'Amenity enhancements', 'Brand repositioning'],
      color: 'from-purple-500/20 to-purple-500/5'
    },
    {
      icon: Users,
      title: 'Revenue Optimization',
      subtitle: 'Lease Strategy & Tenant Mix',
      description: 'Strategic lease restructuring, tenant mix optimization, and occupancy stabilization maximize revenue potential across the portfolio.',
      points: ['Lease optimization', 'Tenant quality upgrades', 'Occupancy stabilization'],
      color: 'from-rose-500/20 to-rose-500/5'
    },
    {
      icon: RefreshCw,
      title: 'Strategic Exits',
      subtitle: 'Timing & Capital Recycling',
      description: 'Market cycle awareness and disciplined disposition timing ensure optimal exit valuations and efficient capital recycling into new opportunities.',
      points: ['Market timing discipline', 'Strategic dispositions', 'Capital redeployment'],
      color: 'from-cyan-500/20 to-cyan-500/5'
    }
  ];

  // Risk management points
  const riskManagement = [
    { icon: Scale, title: 'Conservative Leverage', description: 'Modest debt levels with fixed-rate financing' },
    { icon: Layers, title: 'Diversification', description: 'Multiple property types and submarkets' },
    { icon: BarChart3, title: 'Stress Testing', description: 'Downside scenario analysis on every deal' },
    { icon: Shield, title: 'Capital Reserves', description: 'Adequate reserves for market uncertainty' }
  ];

  // Investor alignment points
  const investorAlignment = [
    'Capital preservation as primary objective',
    'Risk-adjusted returns over speculation',
    'Transparent reporting and communication',
    'GP co-investment in every deal',
    'Long-term partnership approach'
  ];

  return (
    <>
      <Helmet>
        <title>Value-Add Real Estate Investment Strategy | ASAP Capital</title>
        <meta 
          name="description" 
          content="Learn how ASAP Capital creates value through distressed asset acquisitions, multifamily repositioning, and disciplined real estate investment strategies." 
        />
        <meta name="keywords" content="value-add real estate investment strategy, distressed property investing, multifamily investment strategy, private equity real estate, Houston real estate investments" />
        <link rel="canonical" href="/strategy" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-28 lg:pt-44 lg:pb-36 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Investment Strategy</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.3] lg:leading-[1.35] mb-6"
                  variants={itemVariants}
                >
                  Our{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Value-Add
                  </span>{' '}
                  Investment Strategy
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/55 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  How we create value through distressed asset acquisitions, operational improvements, 
                  and disciplined repositioning of multifamily and commercial properties.
                </motion.p>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/portfolio">
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20">
                      View Portfolio
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                    </button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY OVERVIEW ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                {/* Section intro */}
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Our Approach
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Six Pillars of Value Creation
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our value-add real estate investment strategy combines disciplined acquisitions, 
                    operational excellence, and strategic repositioning.
                  </motion.p>
                </div>

                {/* Strategy pillars grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {strategyPillars.map((pillar, index) => (
                    <motion.div
                      key={pillar.title}
                      className="relative bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group overflow-hidden"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}
                    >
                      {/* Background gradient */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${pillar.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                      
                      <div className="relative z-10">
                        {/* Icon */}
                        <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                          <pillar.icon className="h-6 w-6 text-[#D4AF37]" />
                        </div>
                        
                        {/* Title */}
                        <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#D4AF37] transition-colors">
                          {pillar.title}
                        </h3>
                        <p className="text-white/40 text-sm mb-3">{pillar.subtitle}</p>
                        
                        {/* Description */}
                        <p className="text-white/50 text-sm leading-relaxed mb-4">
                          {pillar.description}
                        </p>
                        
                        {/* Points */}
                        <div className="space-y-2">
                          {pillar.points.map((point, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                              <span className="text-white/60">{point}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== RISK MANAGEMENT ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Risk Management
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Disciplined Through Market Cycles
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-10"
                      variants={itemVariants}
                    >
                      Our institutional approach to risk management ensures capital preservation 
                      while pursuing attractive risk-adjusted returns across varying market conditions.
                    </motion.p>
                    
                    {/* Risk management grid */}
                    <motion.div 
                      className="grid sm:grid-cols-2 gap-4"
                      variants={itemVariants}
                    >
                      {riskManagement.map((item, index) => (
                        <motion.div
                          key={item.title}
                          className="bg-[#111D35]/50 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/20 transition-all group"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 5 }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                              <item.icon className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold text-sm mb-1 group-hover:text-[#D4AF37] transition-colors">
                                {item.title}
                              </h4>
                              <p className="text-white/40 text-xs">{item.description}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Visual card */}
                  <motion.div variants={scaleInVariants}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      {/* Grid pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 2px 2px, #D4AF37 1px, transparent 0)`,
                          backgroundSize: '24px 24px'
                        }} />
                      </div>
                      
                      {/* Glow */}
                      <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10 text-center">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 border border-[#D4AF37]/20">
                          <Shield className="h-8 w-8 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Zero Investor Losses</h3>
                        <p className="text-white/50 text-sm mb-6">
                          Track record of capital preservation across 18+ years
                        </p>
                        
                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-[#D4AF37]">70+</p>
                            <p className="text-white/40 text-xs mt-1">Successful Exits</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-white">100%</p>
                            <p className="text-white/40 text-xs mt-1">Capital Return</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTOR ALIGNMENT ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Visual */}
                  <motion.div 
                    className="order-2 lg:order-1"
                    variants={scaleInVariants}
                  >
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <TrendingUp className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Target Returns</h4>
                            <p className="text-white/40 text-sm">Value-Add Strategy</p>
                          </div>
                        </div>
                        
                        {/* Target metrics */}
                        <div className="space-y-4">
                          <div className="bg-[#0A1628]/50 rounded-xl p-4 border border-white/10">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white/60 text-sm">Target IRR</span>
                              <span className="text-[#D4AF37] font-bold text-lg">20-28%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-full"
                                initial={{ width: 0 }}
                                whileInView={{ width: '75%' }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, delay: 0.5 }}
                              />
                            </div>
                          </div>
                          
                          <div className="bg-[#0A1628]/50 rounded-xl p-4 border border-white/10">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white/60 text-sm">Target Multiple</span>
                              <span className="text-white font-bold text-lg">1.8x - 2.5x</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-white/50 to-white/70 rounded-full"
                                initial={{ width: 0 }}
                                whileInView={{ width: '65%' }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, delay: 0.7 }}
                              />
                            </div>
                          </div>
                          
                          <div className="bg-[#0A1628]/50 rounded-xl p-4 border border-white/10">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white/60 text-sm">Preferred Return</span>
                              <span className="text-emerald-400 font-bold text-lg">8%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                                initial={{ width: 0 }}
                                whileInView={{ width: '40%' }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, delay: 0.9 }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className="order-1 lg:order-2">
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Partnership
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Aligned With Investor Objectives
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Our strategy is designed to deliver institutional-quality returns while 
                      prioritizing capital preservation and long-term value creation.
                    </motion.p>
                    
                    {/* Alignment points */}
                    <motion.div className="space-y-3" variants={itemVariants}>
                      {investorAlignment.map((point, index) => (
                        <motion.div
                          key={index}
                          className="flex items-center gap-3"
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/70">{point}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                    
                    <motion.div className="mt-8" variants={itemVariants}>
                      <Link to="/fund-terms">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          View Fund Terms
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PORTFOLIO CTA ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accent */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    See Our Strategy in Action
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Explore Portfolio & Case Studies
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto mb-8"
                    variants={itemVariants}
                  >
                    Review our track record of value-add multifamily investments and see how 
                    our strategy has delivered strong risk-adjusted returns across Houston submarkets.
                  </motion.p>
                  
                  <motion.div 
                    className="flex flex-wrap justify-center gap-4"
                    variants={itemVariants}
                  >
                    <Link to="/portfolio">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20">
                        View Portfolio
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/track-record">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                        Track Record
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-20 lg:py-28 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.h2 
                  className="text-2xl lg:text-3xl font-bold text-white mb-5"
                  variants={itemVariants}
                >
                  Ready to Learn More?
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-8"
                  variants={itemVariants}
                >
                  Schedule a consultation to discuss how our value-add investment strategy 
                  aligns with your portfolio objectives.
                </motion.p>
                
                <motion.div variants={itemVariants}>
                  <Link to="/contact" className="inline-block">
                    <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default StrategyPage;
